import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Ban, Plus, Minus } from "lucide-react";
import client from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReportCenter, REPORTS } from "@/components/dashboard/ReportCenter";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const day = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const err = (x, f) => toast.error(typeof x?.response?.data?.detail === "string" ? x.response.data.detail : f);

const BalanceDialog = ({ agent, type, onClose, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      await client.post(`/tenant-admin/agents/${agent.user_id}/wallet`, { amount: Number(amount), type, note });
      toast.success(type === "credit" ? "Balance added to agent" : "Balance taken back"); onDone();
    } catch (x) { err(x, "Could not update balance"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="agency-agent-balance-dialog">
        <DialogHeader><DialogTitle>{type === "credit" ? "Add balance" : "Take back balance"} — {agent.agency || agent.name}</DialogTitle></DialogHeader>
        <Input type="number" min="1" placeholder="Amount ₹" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="agency-agent-balance-amount" />
        <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="agency-agent-balance-note" />
        <p className="text-xs text-slate-500">{type === "credit" ? "Amount moves from your agency wallet to this agent." : "Amount moves from the agent back to your agency wallet."}</p>
        <Button onClick={save} disabled={!(Number(amount) > 0)} className="bg-[#1B1F5B] hover:bg-[#141844]" data-testid="agency-agent-balance-save">Confirm</Button>
      </DialogContent>
    </Dialog>
  );
};

export const AgencyAgentsPanel = ({ onChange }) => {
  const [agents, setAgents] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => client.get("/tenant-admin/agents").then((r) => setAgents(r.data.agents));
  useEffect(() => { load(); }, []);
  const status = async (a, st) => {
    try { await client.post(`/tenant-admin/agents/${a.user_id}/status`, null, { params: { status: st } }); toast.success(st === "active" ? "Agent approved" : "Agent suspended"); load(); }
    catch (x) { err(x, "Failed"); }
  };
  return (
    <div className="overflow-x-auto rounded-2xl bg-white">
      <p className="border-b px-5 py-3 text-sm text-slate-500">Your B2B agents sign up on your website via <b>Partner Login → Register</b>. Approve them here and load their wallet from your agency wallet.</p>
      <table className="w-full text-left text-sm" data-testid="agency-agents-table">
        <thead className="bg-[#F5F7FB] text-xs uppercase text-slate-500"><tr>{["Agent", "Contact", "PAN / GST", "Status", "Wallet", "Actions"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {agents.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No agents yet</td></tr>}
          {agents.map((a) => (
            <tr key={a.user_id} className="border-t border-slate-100" data-testid={`agency-agent-row-${a.user_id}`}>
              <td className="px-4 py-3"><b className="text-[#1B1F5B]">{a.agency || "-"}</b><div className="text-xs text-slate-500">{a.name}</div></td>
              <td className="px-4 py-3 text-xs">{a.email}<br />{a.phone}</td>
              <td className="px-4 py-3 text-xs">{a.kyc?.pan || "-"}<br />{a.kyc?.gst || ""}</td>
              <td className="px-4 py-3 capitalize" data-testid={`agency-agent-status-${a.user_id}`}>{a.status}</td>
              <td className="px-4 py-3 font-bold" data-testid={`agency-agent-wallet-${a.user_id}`}>{inr(a.wallet)}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {a.status !== "active" ? (
                    <Button size="sm" className="h-7 bg-[#0E7C6B] px-2 hover:bg-[#0A5348]" onClick={() => status(a, "active")} data-testid={`agency-agent-approve-${a.user_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => status(a, "suspended")} data-testid={`agency-agent-suspend-${a.user_id}`}><Ban className="mr-1 h-3.5 w-3.5" />Suspend</Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 px-2" disabled={a.status !== "active"} onClick={() => setDlg({ agent: a, type: "credit" })} data-testid={`agency-agent-add-${a.user_id}`}><Plus className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setDlg({ agent: a, type: "debit" })} data-testid={`agency-agent-deduct-${a.user_id}`}><Minus className="h-3.5 w-3.5" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dlg && <BalanceDialog {...dlg} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); onChange?.(); }} />}
    </div>
  );
};

export const AgencyWalletPanel = () => {
  const [w, setW] = useState(null);
  useEffect(() => { client.get("/tenant-admin/wallet").then((r) => setW(r.data)); }, []);
  if (!w) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 rounded-2xl bg-[#1B1F5B] p-6 text-white">
        <Wallet className="h-9 w-9 text-[#F5B32B]" />
        <div><p className="text-xs uppercase tracking-widest text-white/60">Agency wallet with FLYTRIP</p><p className="text-3xl font-extrabold" data-testid="agency-wallet-balance">{inr(w.wallet)}</p></div>
        <p className="ml-auto max-w-xs text-xs text-white/70">Your agents' bookings are deducted at net fare. To top up, deposit with FLYTRIP — the platform credits it here.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl bg-white">
        <table className="w-full text-left text-sm" data-testid="agency-wallet-table">
          <thead className="bg-[#F5F7FB] text-xs uppercase text-slate-500"><tr>{["Date", "Narration", "Credit", "Debit", "Balance"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {w.transactions.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No transactions yet</td></tr>}
            {w.transactions.map((t) => (
              <tr key={t.txn_id} className="border-t border-slate-100">
                <td className="px-4 py-2.5">{day(t.created_at)}</td><td className="px-4 py-2.5">{t.note}</td>
                <td className="px-4 py-2.5 text-[#0E7C6B]">{t.type === "credit" ? inr(t.amount) : ""}</td>
                <td className="px-4 py-2.5 text-red-600">{t.type === "debit" ? inr(t.amount) : ""}</td>
                <td className="px-4 py-2.5 font-semibold">{inr(t.balance_after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AgencyReportsPanel = () => {
  const [kind, setKind] = useState("flight");
  return (
    <div className="grid gap-6 lg:grid-cols-[200px_1fr]">
      <div className="space-y-1 rounded-xl bg-[#E8EEFB] p-2">
        {Object.entries(REPORTS).map(([k, r]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`agency-report-${k}`}
            className={`block w-full rounded-md px-3 py-2.5 text-left text-sm ${kind === k ? "border-l-4 border-[#1B1F5B] bg-white font-semibold text-[#1B1F5B]" : "text-slate-700 hover:bg-white/70"}`}>{r.title}</button>
        ))}
      </div>
      <ReportCenter key={kind} kind={kind} admin />
    </div>
  );
};
